import { Injectable } from '@angular/core';
import { FuseNavigationItem, FuseNavigationService, FuseVerticalNavigationComponent } from '@fuse/components/navigation';
import { FuseMockApiService } from '@fuse/lib/mock-api';
import {
    affiliateNavigation,
    compactNavigation,
    defaultNavigation,
    futuristicNavigation,
    horizontalNavigation,
} from 'app/mock-api/common/navigation/data';
import { User } from 'app/core/user/user.types';
import { UserService } from 'app/core/user/user.service';
import { SubscriptionStatusEnum } from 'app/modules/shared/enums/subscriptionStatusEnum';
import { Route, Router } from '@angular/router';
import { PathEnum } from 'app/modules/shared/enums/pathEnum';
import { Subscription } from 'app/modules/shared/models/subscription';
import { cloneDeep } from 'lodash-es';
import { appRoutes } from 'app/app.routes';
import { UserUtil } from 'app/modules/shared/utils/user.util';
import { PlanEnum } from 'app/modules/shared/enums/planEnum';
import { Plan } from 'app/modules/shared/models/plan';
import { PlanUtil } from 'app/modules/shared/utils/plan.util';
import { NoAuthService } from 'app/modules/shared/services/noAuth.service';
import { SubscriptionService } from 'app/modules/partner/services/subscription.service';
import { PlanService } from 'app/modules/partner/services/plan.service';

@Injectable({ providedIn: 'root' })
export class NavigationMockApi {
    private readonly _compactNavigation: FuseNavigationItem[] =
        compactNavigation;
    private readonly _defaultNavigation: FuseNavigationItem[] =
        defaultNavigation;
    private readonly _futuristicNavigation: FuseNavigationItem[] =
        futuristicNavigation;
    private readonly _horizontalNavigation: FuseNavigationItem[] =
        horizontalNavigation;
    user: User;
    partnerSubscription: Subscription;
    appRoutes: Route[];
    plans: Plan[];

    /**
     * Constructor
     */
    constructor(
        private _fuseMockApiService: FuseMockApiService,
        private _userService: UserService,
        private _subscriptionService: SubscriptionService,
        private _noAuthService: NoAuthService,
        private _router: Router,
        private _fuseNavigationService: FuseNavigationService,
        private _planService: PlanService
        )
    {
        this._userService.user$
        .subscribe((user: User) => {
            this.user = user;
        });

        this._subscriptionService.partnerSubscription$
        .subscribe((partnerSubscription: Subscription) => {
            this.partnerSubscription = partnerSubscription;
            this.refreshNavigation();  // Appeler une fonction de rafraîchissement chaque fois que l'abonnement change
        });

        // Load plans for partner users navigation checks
        this._noAuthService.getAllPlansNoAuth().subscribe((plans: Plan[]) => {
            this.plans = plans;
            // Initialize plans in plan service
            this._planService.allPlans = plans;
        });

        // Register Mock API handlers
        this.registerHandlers();
        this.appRoutes = appRoutes;
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    getStatusNavigation(partnerSubscription: Subscription, currentRouteNavigation: Route): boolean {
        if (partnerSubscription && partnerSubscription.subscriptionStatusRefCode && partnerSubscription.planId) {
            let res : boolean = true;
            const subscriptionStatus = this.checkSubscriptionStatus(partnerSubscription, currentRouteNavigation);
            const subscribedPlanStatus = this.checkSubscribedPlan(partnerSubscription, currentRouteNavigation);
            // if subscriptionStatus or subscribedPlanStatus is not good, we disable the feature
            return subscriptionStatus || subscribedPlanStatus;            
        } else {
            return true;
        }
    }

    private checkSubscriptionStatus(subscription: Subscription, currentRouteNavigation: Route): boolean {
        // Check subscription status
        const allowedSubscriptionStatus: string[] = currentRouteNavigation.data['allowedSubscriptionStatus'];
        if(allowedSubscriptionStatus != null && typeof allowedSubscriptionStatus != 'undefined' ){
            if (allowedSubscriptionStatus.find(x => (x === SubscriptionStatusEnum.ALL))) {
                return false;
            } else if (allowedSubscriptionStatus.find(x => x === subscription.subscriptionStatusRefCode)) {
                return false;
            } else {
                return true;
            }
        } else {
            console.error('Subscription status problem !');
            this._router.navigate([PathEnum.ERROR, PathEnum.ERROR_403]);
        }        
    }

    private checkSubscribedPlan(subscription: Subscription, currentRoutesNavigation: Route): boolean {
        // Check subscription plan
        const planSubscribed : Plan = PlanUtil.getPlanById(subscription.planId, this.plans);
        const allowedPlan: string = currentRoutesNavigation.data['allowedPlans'];
        if(allowedPlan != null && typeof allowedPlan != 'undefined' ){
            if (allowedPlan === PlanEnum.ALL) {
                return false;
            } else {
                // If planSubscribed level is superior to allowedPlan the the user can access to the feature
                if(planSubscribed.planLevel >=  PlanUtil.getPlanByCode(allowedPlan, this.plans).planLevel){
                    return false;
                } else {
                    return true;
                }
            }
        } else {
            console.error('Plan susbscribed problem !');
            this._router.navigate([PathEnum.ERROR, PathEnum.ERROR_403]);
        }
    }

    /**
     * Register Mock API handlers
     */
    registerHandlers(): void {
        // -----------------------------------------------------------------------------------------------------
        // @ Navigation - GET
        // -----------------------------------------------------------------------------------------------------
        this._fuseMockApiService.onGet('api/common/navigation').reply(() => {
            this.manageNavigation();
            // Affiliate users have their own dedicated navigation. Returned across all 4
            // layout variants — affiliates only see affiliate-portal items, never team /
            // partner ones.
            if (UserUtil.isAffiliateUser(this.user)) {
                const affiliateNav = cloneDeep(affiliateNavigation);
                return [
                    200,
                    {
                        compact: affiliateNav,
                        default: affiliateNav,
                        futuristic: affiliateNav,
                        horizontal: affiliateNav,
                    },
                ];
            }
            // Return the response
            return [
                200,
                {
                    compact: cloneDeep(this._compactNavigation),
                    default: cloneDeep(this._defaultNavigation),
                    futuristic: cloneDeep(this._futuristicNavigation),
                    horizontal: cloneDeep(this._horizontalNavigation),
                },
            ];
        });
    }

    private refreshNavigation(): void {
        // Ici, nous supposons que "registerHandlers" ou une méthode similaire 
        // est capable de rafraîchir les données de navigation en fonction de l'état actuel
        this.manageNavigation();

        // Get the component -> navigation data -> item
        const navComponent =
            this._fuseNavigationService.getComponent<FuseVerticalNavigationComponent>(
                'mainNavigation'
            );

        // Return if the navigation component does not exist
        if (!navComponent) {
            return null;
        }

        // Get the flat navigation data
        const navigation = navComponent.navigation;
       
        navigation.map((nav) => {
            const item = this._fuseNavigationService.getItem(nav.id, navigation);
            if (item)
            {
                this._defaultNavigation.map(defaultNavigationItem => {
                    if (defaultNavigationItem.id === item.id){
                        this.processUpdateToBeRefreshed(item, defaultNavigationItem);
                    }
                });

                this.updateChildrenItem(nav, item, navigation);
            }
            navComponent.refresh();        
        })
    }

    updateChildrenItem(nav: any, item: any, navigation: any) {
        if (item.children && item.children.length > 0) {
            item.children.map((child) => {
                const itemChild = this._fuseNavigationService.getItem(child.id, navigation);
                this._defaultNavigation.map(defaultNavigationItem => {
                    if(defaultNavigationItem.children && defaultNavigationItem.children.length > 0){
                        defaultNavigationItem.children.map((defaultNavigationItemChild) => {
                            if (defaultNavigationItemChild.id === itemChild.id){
                                this.processUpdateToBeRefreshed(item, defaultNavigationItem);
                            }   
                        });
                    }
                });
                this.updateChildrenItem(nav, child, navigation);
            });
        }
    }

    private processUpdateToBeRefreshed(item, defaultNavigationItem){
        // Refresh disabled
        item.disabled = defaultNavigationItem.disabled;
    }

    private manageNavigation():void{

            // Hide some FuseNavigationItem depending user roles
            this._defaultNavigation.map((elem) => {
                if(elem.hidden != undefined){
                    elem.meta = {
                        displayFor: this.user
                    };
                }
                if (elem.id !== 'error' && elem.id !== 'home') {
                    const currentRoutesNavigation = this.appRoutes.filter(x => x.path === PathEnum.APP)[1].children.find(x => x.path === elem.link); 
                    if(!elem.hidden(elem) && UserUtil.isPartnerUser(this.user)){ // Check subscription status only for Partners
                        elem.disabled = this.getStatusNavigation(this.partnerSubscription, currentRoutesNavigation);
                    }
                }
            });

            // Hide some FuseNavigationItem depending user roles
            this._compactNavigation.map((elem) => {
                if (elem.hidden != undefined){
                    elem.meta = {
                        displayFor: this.user
                    };
                }
                if (elem.id !== 'error' && elem.id !== 'home') {
                    const currentRoutesNavigation = this.appRoutes.filter(x => x.path === PathEnum.APP)[1].children.find(x => x.path === elem.link); 
                    if(!elem.hidden(elem) && UserUtil.isPartnerUser(this.user)){ // Check subscription status only for Partners
                        elem.disabled = this.getStatusNavigation(this.partnerSubscription, currentRoutesNavigation);
                    }
                }
            });

            // Hide some FuseNavigationItem depending user roles
            this._futuristicNavigation.map((elem) => {
                if(elem.hidden != undefined){
                    elem.meta = {
                        displayFor: this.user
                    };
                }
                if (elem.id !== 'error' && elem.id !== 'home') {
                    const currentRoutesNavigation = this.appRoutes.filter(x => x.path === PathEnum.APP)[1].children.find(x => x.path === elem.link); 
                    if(!elem.hidden(elem) && UserUtil.isPartnerUser(this.user)){ // Check subscription status only for Partners
                        elem.disabled = this.getStatusNavigation(this.partnerSubscription, currentRoutesNavigation);
                    }
                }
            });

            // Hide some FuseNavigationItem depending user roles
            this._horizontalNavigation.map((elem) => {
                if(elem.hidden != undefined){
                    elem.meta = {
                        displayFor: this.user
                    };
                }
                if (elem.id !== 'error' && elem.id !== 'home') {
                    const currentRoutesNavigation = this.appRoutes.filter(x => x.path === PathEnum.APP)[1].children.find(x => x.path === elem.link); 
                    if(!elem.hidden(elem) && UserUtil.isPartnerUser(this.user)){ // Check subscription status only for Partners
                        elem.disabled = this.getStatusNavigation(this.partnerSubscription, currentRoutesNavigation);
                    }
                }
            });
            
            let navigationItem = this._defaultNavigation;
            // Fill compact navigation children using the default navigation
            this._compactNavigation.forEach((compactNavItem) => {
                navigationItem.forEach((defaultNavItem) => {
                    if ( defaultNavItem.id === compactNavItem.id )
                    {
                        compactNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });

            // Fill futuristic navigation children using the default navigation
            this._futuristicNavigation.forEach((futuristicNavItem) => {
                navigationItem.forEach((defaultNavItem) => {
                    if ( defaultNavItem.id === futuristicNavItem.id )
                    {
                        futuristicNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });

            // Fill horizontal navigation children using the default navigation
            this._horizontalNavigation.forEach((horizontalNavItem) => {
                navigationItem.forEach((defaultNavItem) => {
                    if (defaultNavItem.id === horizontalNavItem.id )
                    {
                        horizontalNavItem.children = cloneDeep(defaultNavItem.children);
                    }
                });
            });

    }
}
